import { throttle } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Dimensions, ScaledSize } from 'react-native';

type Breakpoint<T extends string | number> = { key: T; width: number };

export const getBreakpoint = <T extends string | number>(
  breakpoints: Breakpoint<T>[],
  width: number,
): T | null => {
  return (
    breakpoints.reduce<Breakpoint<T> | null>(
      (acc, breakpoint) =>
        breakpoint.width <= width && (!acc || acc.width < breakpoint.width)
          ? breakpoint
          : acc,
      null,
    )?.key || null
  );
};

export const useScreenBreakpoints = <T extends string | number>(
  breakpoints: Breakpoint<T>[],
  onChangeThrottle: number = 250,
): T | null => {
  const initBreakpointKey = useMemo(
    () => getBreakpoint(breakpoints, Dimensions.get('window').width),
    [breakpoints],
  );

  const [breakpointKey, setBreakpointKey] = useState(initBreakpointKey);

  const onChange = useMemo(
    () =>
      throttle(
        ({ window: { width } }: { window: ScaledSize }) =>
          setBreakpointKey(getBreakpoint(breakpoints, width)),
        onChangeThrottle,
      ),
    [breakpoints, onChangeThrottle],
  );

  useEffect(() => {
    const listener = Dimensions.addEventListener('change', onChange);
    return () => {
      listener.remove();
    };
  }, [onChange]);

  return breakpointKey;
};
